import { useIsIntegrationDescriptionRequired } from '@modules/integrationsV2/hooks';
import { useFetchSampleDescriptions } from '@modules/integrationsV2/hooks/useFetchSampleDescriptions';
import { useValidateIntegrationDescription } from '@modules/integrationsV2/hooks/useValidateIntegrationDescription';
import { IntegrationChannelType, IntegrationType } from 'generated/graphql';
import { useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'react-use';

export const MAX_DESCRIPTION_ALLOWED_CHARS = 500;

interface ValidateIntegrationFormOptions {
  channelType: IntegrationChannelType;
  integrationType: IntegrationType;
  hasValidFormFields: boolean;
  descriptionInput: string;
  currentDescription: string;
  onValidated?: () => Promise<void>;
}

export const useValidateIntegrationForm = ({
  channelType,
  integrationType,
  hasValidFormFields: hasValidFormFieldsProp,
  descriptionInput,
  currentDescription,
  onValidated,
}: ValidateIntegrationFormOptions) => {
  const { sampleDescriptions } = useFetchSampleDescriptions();

  const [lastValidatedDescription, setLastValidatedDescription] =
    useState(currentDescription);

  useEffect(() => {
    setLastValidatedDescription(currentDescription);
  }, [currentDescription]);

  const descriptionValue = descriptionInput.trim();

  const [
    validateDescription,
    {
      isLoading: isValidatingDescription,
      validForQuestions: descriptionValidForQuestions,
      message: validatedDescriptionMessage,
    },
  ] = useValidateIntegrationDescription({
    onCompleted: () => {
      setLastValidatedDescription(descriptionValue);
    },
  });

  const isDescriptionRequired = useIsIntegrationDescriptionRequired({
    channelType,
    integrationType,
  });

  const isSameAsCurrentDescription = currentDescription.trim()
    ? currentDescription === descriptionValue
    : false;

  const isSameAsSampleDescription = sampleDescriptions.some(
    (description) => description.description === descriptionValue,
  );

  const isPreValidated =
    isSameAsCurrentDescription || isSameAsSampleDescription;

  const isCheckedForValidation = descriptionValue === lastValidatedDescription;

  useDebounce(
    () => {
      if (descriptionValue && !isPreValidated && !isCheckedForValidation) {
        validateDescription(descriptionValue);
      }
    },
    500,
    [descriptionValue],
  );

  const isValidForQuestions = isCheckedForValidation
    ? descriptionValidForQuestions.every((question) => question.isValid)
    : true;

  const hasValidFormFields =
    hasValidFormFieldsProp &&
    descriptionValue.length <= MAX_DESCRIPTION_ALLOWED_CHARS &&
    !isValidatingDescription &&
    isValidForQuestions;

  const isDisabled = isDescriptionRequired
    ? !descriptionValue || !hasValidFormFields
    : !hasValidFormFields;

  const validateForm = useCallback(async () => {
    if (isDisabled) {
      return;
    }

    let isValid = true;

    if (descriptionValue && isCheckedForValidation) {
      isValid = isValidForQuestions;
    }

    if (onValidated && isValid) {
      await onValidated();
    }

    return isValid;
  }, [
    descriptionValue,
    isDisabled,
    isCheckedForValidation,
    isValidForQuestions,
    onValidated,
  ]);

  return [
    validateForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions: isCheckedForValidation
        ? descriptionValidForQuestions
        : [],
      validatedDescriptionMessage: isCheckedForValidation
        ? validatedDescriptionMessage
        : '',
      isPreValidated,
    },
  ] as const;
};
